import { useState, useEffect, useCallback } from "react";
import {
  UserProgress,
  EducationStats,
  EducationData,
  LessonNote,
  LessonCompletionCriteria,
  LessonRating,
  LessonSurvey,
  LessonComment,
} from "../types/education";
import { progressTrackingService } from "../services/progressTrackingService";
import {
  skillAssessmentService,
  ProgressAnalytics,
} from "../services/skillAssessmentService";
import { CurriculumService } from "../services/curriculumService";

const DEFAULT_STATS: EducationStats = {
  totalLessons: 0,
  completedLessons: 0,
  totalTimeSpent: 0,
  averageQuizScore: 0,
  currentStreak: 0,
  longestStreak: 0,
};

const DEFAULT_DATA: EducationData = {
  progress: {},
  stats: DEFAULT_STATS,
  achievements: [],
  bookmarks: [],
  notes: [],
  noteTags: [],
  ratings: [],
  surveys: [],
  comments: [],
};

interface UseEducationProgressOptions {
  enableCloudSync?: boolean;
  enableSkillAssessment?: boolean;
  curriculumService?: CurriculumService;
}

export const useEducationProgress = (
  options: UseEducationProgressOptions = {},
) => {
  const [educationData, setEducationData] =
    useState<EducationData>(DEFAULT_DATA);
  const [isLoading, setIsLoading] = useState(true);
  const [analytics, setAnalytics] = useState<ProgressAnalytics | null>(null);
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncError, setSyncError] = useState<string | null>(null);

  const {
    enableCloudSync = false,
    enableSkillAssessment = false,
    curriculumService,
  } = options;

  // Initialize progress tracking service configuration
  useEffect(() => {
    progressTrackingService.updateConfig({
      enableCloudSync,
      offlineMode: false,
    });
  }, [enableCloudSync]);

  // Load initial data
  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);
        const data = await progressTrackingService.loadProgress();

        if (curriculumService) {
          const allLessonIds = new Set<string>();
          curriculumService.getAllTopics().forEach((topic) => {
            curriculumService.getLessonsForTopic(topic.id).forEach((lesson) => {
              allLessonIds.add(lesson.id);
            });
          });
          data.stats.totalLessons = allLessonIds.size;
        }

        setEducationData(data);

        // Generate analytics if skill assessment is enabled and curriculum service is provided
        if (enableSkillAssessment && curriculumService) {
          const progressAnalytics =
            skillAssessmentService.generateProgressAnalytics(
              data,
              curriculumService,
            );
          setAnalytics(progressAnalytics);
        }
      } catch (error) {
        console.error("Failed to load education progress:", error);
        setEducationData(DEFAULT_DATA);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [enableSkillAssessment, curriculumService]);

  // Save data using progress tracking service
  const saveData = useCallback(
    async (newData: EducationData) => {
      try {
        setEducationData(newData);
        await progressTrackingService.saveProgress(newData);

        // Update analytics if enabled
        if (enableSkillAssessment && curriculumService) {
          const progressAnalytics =
            skillAssessmentService.generateProgressAnalytics(
              newData,
              curriculumService,
            );
          setAnalytics(progressAnalytics);
        }
      } catch (error) {
        console.error("Failed to save education progress:", error);
        throw error;
      }
    },
    [enableSkillAssessment, curriculumService],
  );

  // Update lesson progress with enhanced functionality
  const updateLessonProgress = useCallback(
    async (lessonId: string, progressUpdate: Partial<UserProgress>) => {
      setEducationData((currentData) => {
        const currentProgress = currentData.progress[lessonId] || {
          lessonId,
          progress: 0,
          completed: false,
          timeSpent: 0,
          quizScores: [],
          lastAccessed: new Date().toISOString(),
        };

        const updatedProgress: UserProgress = {
          ...currentProgress,
          ...progressUpdate,
          lessonId: currentProgress.lessonId,
          lastAccessed: new Date().toISOString(),
          quizScores: progressUpdate.quizScores
            ? progressUpdate.quizScores
            : currentProgress.quizScores,
        };

        // Create new progress object including the updated lesson
        const newProgress = {
          ...currentData.progress,
          [lessonId]: updatedProgress,
        };

        // Update stats based on new progress
        const updatedStats = {
          ...currentData.stats,
          completedLessons: Object.values(newProgress).filter(
            (p) => p.completed,
          ).length,
          totalTimeSpent: Object.values(newProgress).reduce(
            (sum, p) => sum + p.timeSpent,
            0,
          ),
        };

        // Calculate average quiz score
        const allScores = Object.values(newProgress).flatMap(
          (p) => p.quizScores || [],
        );
        if (allScores.length > 0) {
          updatedStats.averageQuizScore = Math.round(
            allScores.reduce((sum, score) => sum + score, 0) / allScores.length,
          );
        }

        const newData = {
          ...currentData,
          progress: newProgress,
          stats: updatedStats,
        };

        // Save asynchronously without blocking
        saveData(newData).catch((error) => {
          console.error("Failed to save progress update:", error);
        });

        return newData;
      });
    },
    [saveData],
  );

  // Check if lesson prerequisites are met
  const checkPrerequisites = useCallback(
    (lessonId: string, prerequisites: string[]): boolean => {
      if (!curriculumService || prerequisites.length === 0) {
        return true;
      }

      return prerequisites.every((prereqId) => {
        const prereqProgress = educationData.progress[prereqId];
        return prereqProgress?.completed === true;
      });
    },
    [educationData.progress, curriculumService],
  );

  // Get prerequisite status for a lesson
  const getPrerequisiteStatus = useCallback(
    (lessonId: string, prerequisites: string[]) => {
      if (!curriculumService || prerequisites.length === 0) {
        return { allMet: true, missingPrereqs: [] };
      }

      const missingPrereqs = prerequisites.filter((prereqId) => {
        const prereqProgress = educationData.progress[prereqId];
        return !prereqProgress?.completed;
      });

      return {
        allMet: missingPrereqs.length === 0,
        missingPrereqs,
      };
    },
    [educationData.progress, curriculumService],
  );

  // Check if lesson can be accessed
  const canAccessLesson = useCallback(
    (lessonId: string, prerequisites: string[] = []): boolean => {
      return checkPrerequisites(lessonId, prerequisites);
    },
    [checkPrerequisites],
  );

  // Validate lesson completion requirements with detailed feedback
  const validateLessonCompletion = useCallback(
    (
      criteria: LessonCompletionCriteria,
      completedPages: Set<string>,
      completedElements: Set<string>,
      currentTimeSpent: number,
      quizScores: number[] = [],
    ): {
      canComplete: boolean;
      missingRequirements: string[];
      progressDetails: {
        pagesCompleted: number;
        pagesRequired: number;
        timeSpent: number;
        timeRequired?: number;
        quizScore?: number;
        quizScoreRequired?: number;
        elementsCompleted: number;
        elementsRequired: number;
      };
    } => {
      const missingRequirements: string[] = [];

      // Check required pages
      const pagesCompleted = criteria.requiredPages.filter((pageId) =>
        completedPages.has(pageId),
      ).length;
      const pagesRequired = criteria.requiredPages.length;

      if (pagesCompleted < pagesRequired) {
        missingRequirements.push(
          `Complete ${pagesRequired - pagesCompleted} more required page(s)`,
        );
      }

      // Check minimum time spent
      const timeSpent = currentTimeSpent;
      const timeRequired = criteria.minimumTimeSpent;

      if (timeRequired && timeSpent < timeRequired) {
        missingRequirements.push(
          `Spend ${Math.ceil((timeRequired - timeSpent) / 60)} more minutes in the lesson`,
        );
      }

      // Check quiz scores
      let quizScore = 0;
      const quizScoreRequired = criteria.minimumQuizScore;

      if (quizScoreRequired && quizScores.length > 0) {
        quizScore = Math.round(
          quizScores.reduce((sum, score) => sum + score, 0) / quizScores.length,
        );
        if (quizScore < quizScoreRequired) {
          missingRequirements.push(
            `Achieve a quiz score of at least ${quizScoreRequired}% (current: ${quizScore}%)`,
          );
        }
      } else if (quizScoreRequired) {
        missingRequirements.push(
          `Complete quiz assessments to achieve minimum score of ${quizScoreRequired}%`,
        );
      }

      // Check required interactive elements
      const elementsCompleted =
        criteria.requiredInteractiveElements?.filter((elementId) =>
          completedElements.has(elementId),
        ).length || 0;
      const elementsRequired =
        criteria.requiredInteractiveElements?.length || 0;

      if (elementsCompleted < elementsRequired) {
        missingRequirements.push(
          `Complete ${elementsRequired - elementsCompleted} more required interactive exercise(s)`,
        );
      }

      // Check custom criteria
      criteria.customCriteria?.forEach((custom) => {
        switch (custom.type) {
          case "score":
            // This would need additional data from interactive elements
            missingRequirements.push(
              custom.description || "Complete custom scoring requirement",
            );
            break;
          case "completion":
            // This would need additional completion tracking
            missingRequirements.push(
              custom.description || "Complete custom requirement",
            );
            break;
          case "manual":
            // Manual validation (e.g., instructor approval)
            missingRequirements.push(
              custom.description || "Manual approval required",
            );
            break;
        }
      });

      return {
        canComplete: missingRequirements.length === 0,
        missingRequirements,
        progressDetails: {
          pagesCompleted,
          pagesRequired,
          timeSpent,
          timeRequired,
          quizScore: quizScores.length > 0 ? quizScore : undefined,
          quizScoreRequired,
          elementsCompleted,
          elementsRequired,
        },
      };
    },
    [],
  );

  // Get lesson flow status
  const getLessonFlowStatus = useCallback(
    (
      lessonId: string,
      prerequisites: string[] = [],
      completionCriteria?: LessonCompletionCriteria,
    ) => {
      const prereqStatus = getPrerequisiteStatus(lessonId, prerequisites);
      const lessonProgress = educationData.progress[lessonId];

      let canComplete = false;
      let validationDetails = null;

      if (lessonProgress && completionCriteria) {
        const completedPages = new Set(lessonProgress.completedPages || []);
        const completedElements = new Set(
          lessonProgress.completedElements || [],
        );
        const quizScores = lessonProgress.quizScores || [];

        validationDetails = validateLessonCompletion(
          completionCriteria,
          completedPages,
          completedElements,
          lessonProgress.timeSpent,
          quizScores,
        );
        canComplete = validationDetails.canComplete;
      }

      return {
        canAccess: prereqStatus.allMet,
        missingPrerequisites: prereqStatus.missingPrereqs,
        isStarted: !!lessonProgress,
        isCompleted: lessonProgress?.completed || false,
        progress: lessonProgress?.progress || 0,
        canComplete,
        validationDetails,
      };
    },
    [getPrerequisiteStatus, educationData.progress, validateLessonCompletion],
  );

  // Update page completion status
  const updatePageCompletion = useCallback(
    async (lessonId: string, pageId: string, completed: boolean) => {
      const currentProgress = educationData.progress[lessonId];
      if (!currentProgress) return;

      const completedPages = new Set(currentProgress.completedPages || []);

      if (completed) {
        completedPages.add(pageId);
      } else {
        completedPages.delete(pageId);
      }

      await updateLessonProgress(lessonId, {
        completedPages: Array.from(completedPages),
      });
    },
    [educationData.progress, updateLessonProgress],
  );

  // Check if user can navigate to next page
  const canNavigateToPage = useCallback(
    (
      lessonId: string,
      pageIndex: number,
      totalPages: number,
      requiredPages: string[] = [],
    ): boolean => {
      if (pageIndex === 0) return true; // Always can access first page if lesson is accessible

      const lessonProgress = educationData.progress[lessonId];
      if (!lessonProgress) return false;

      // Check if previous required pages are completed
      const completedPages = new Set(lessonProgress.completedPages || []);

      for (let i = 0; i < pageIndex; i++) {
        const pageId = requiredPages[i] || `page-${i}`;
        if (requiredPages.includes(pageId) && !completedPages.has(pageId)) {
          return false;
        }
      }

      return true;
    },
    [educationData.progress],
  );
  const markLessonCompleted = useCallback(
    async (lessonId: string) => {
      await updateLessonProgress(lessonId, {
        completed: true,
        progress: 100,
      });
    },
    [updateLessonProgress],
  );

  // Add quiz score
  const addQuizScore = useCallback(
    async (lessonId: string, score: number) => {
      setEducationData((currentData) => {
        const currentProgress = currentData.progress[lessonId] || {
          lessonId,
          progress: 0,
          completed: false,
          timeSpent: 0,
          quizScores: [],
          lastAccessed: new Date().toISOString(),
        };

        const updatedProgress = {
          ...currentProgress,
          quizScores: [...(currentProgress.quizScores || []), score],
          lastAccessed: new Date().toISOString(),
        };

        // Update average quiz score
        const allScores = Object.values(currentData.progress).flatMap(
          (p) => p.quizScores || [],
        );
        const newAllScores = [...allScores, score];
        const averageQuizScore = Math.round(
          newAllScores.reduce((sum, s) => sum + s, 0) / newAllScores.length,
        );

        const newData = {
          ...currentData,
          progress: {
            ...currentData.progress,
            [lessonId]: updatedProgress,
          },
          stats: {
            ...currentData.stats,
            averageQuizScore,
          },
        };

        // Save asynchronously
        saveData(newData).catch((error) => {
          console.error("Failed to save quiz score:", error);
        });

        return newData;
      });
    },
    [saveData],
  );

  // Add bookmark
  const addBookmark = useCallback(
    async (lessonId: string) => {
      setEducationData((currentData) => {
        const newData = {
          ...currentData,
          bookmarks: [...new Set([...currentData.bookmarks, lessonId])],
        };

        saveData(newData).catch((error) => {
          console.error("Failed to add bookmark:", error);
        });

        return newData;
      });
    },
    [saveData],
  );

  // Remove bookmark
  const removeBookmark = useCallback(
    async (lessonId: string) => {
      setEducationData((currentData) => {
        const newData = {
          ...currentData,
          bookmarks: currentData.bookmarks.filter((id) => id !== lessonId),
        };

        saveData(newData).catch((error) => {
          console.error("Failed to remove bookmark:", error);
        });

        return newData;
      });
    },
    [saveData],
  );

  // Check if lesson is bookmarked
  const isBookmarked = useCallback(
    (lessonId: string) => {
      return educationData.bookmarks.includes(lessonId);
    },
    [educationData.bookmarks],
  );

  // Sync with cloud
  const syncWithCloud = useCallback(async () => {
    if (!enableCloudSync) return;

    try {
      setIsSyncing(true);
      setSyncError(null);
      const result = await progressTrackingService.syncWithCloud();

      if (result.success) {
        // Reload data after successful sync
        const data = await progressTrackingService.loadProgress();
        setEducationData(data);

        if (enableSkillAssessment && curriculumService) {
          const progressAnalytics =
            skillAssessmentService.generateProgressAnalytics(
              data,
              curriculumService,
            );
          setAnalytics(progressAnalytics);
        }
      } else {
        setSyncError(result.errors.join(", "));
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Sync failed";
      setSyncError(errorMessage);
    } finally {
      setIsSyncing(false);
    }
  }, [enableCloudSync, enableSkillAssessment, curriculumService]);

  // Create backup
  const createBackup = useCallback(async () => {
    try {
      const backupId = await progressTrackingService.createBackup();
      return backupId;
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Backup failed";
      throw new Error(errorMessage);
    }
  }, []);

  // Restore backup
  const restoreBackup = useCallback(
    async (backupId: string) => {
      try {
        await progressTrackingService.restoreBackup(backupId);
        const data = await progressTrackingService.loadProgress();
        setEducationData(data);

        if (enableSkillAssessment && curriculumService) {
          const progressAnalytics =
            skillAssessmentService.generateProgressAnalytics(
              data,
              curriculumService,
            );
          setAnalytics(progressAnalytics);
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Restore failed";
        throw new Error(errorMessage);
      }
    },
    [enableSkillAssessment, curriculumService],
  );

  // Get skill metrics (if skill assessment is enabled)
  const getSkillMetrics = useCallback(
    (skillId: string) => {
      if (!enableSkillAssessment || !curriculumService) return null;
      return skillAssessmentService.assessSkillLevel(
        skillId,
        educationData,
        curriculumService,
      );
    },
    [enableSkillAssessment, curriculumService, educationData],
  );

  // Get mastery score (if skill assessment is enabled)
  const getMasteryScore = useCallback(
    (skillId: string) => {
      if (!enableSkillAssessment || !curriculumService) return 0;
      return skillAssessmentService.calculateMasteryScore(
        skillId,
        educationData,
        curriculumService,
      );
    },
    [enableSkillAssessment, curriculumService, educationData],
  );

  // Get learning recommendations (if skill assessment is enabled)
  const getLearningRecommendations = useCallback(() => {
    if (!enableSkillAssessment || !curriculumService) return [];
    return skillAssessmentService.generateLearningRecommendations(
      educationData,
      curriculumService,
    );
  }, [enableSkillAssessment, curriculumService, educationData]);

  // Export/Import progress data
  const exportProgress = useCallback(async () => {
    try {
      const backupId = await progressTrackingService.createBackup();
      return backupId;
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Export failed";
      throw new Error(errorMessage);
    }
  }, []);

  const importProgress = useCallback(
    async (backupId: string) => {
      try {
        await restoreBackup(backupId);
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Import failed";
        throw new Error(errorMessage);
      }
    },
    [restoreBackup],
  );

  // Legacy export/import methods for backward compatibility
  const exportProgressData = useCallback(() => {
    return JSON.stringify(educationData);
  }, [educationData]);

  const importProgressData = useCallback(
    (jsonData: string) => {
      try {
        const data = JSON.parse(jsonData);
        if (data && typeof data === "object" && data.progress && data.stats) {
          saveData(data);
          return true;
        }
        return false;
      } catch (error) {
        console.error("Failed to import progress data:", error);
        return false;
      }
    },
    [saveData],
  );

  // Reset progress to default state
  const resetProgress = useCallback(async () => {
    await saveData(DEFAULT_DATA);
  }, [saveData]);

  // Note management methods
  const addNote = useCallback(
    async (
      lessonId: string,
      content: string,
      pageId?: string,
      tags?: string[],
    ) => {
      const newNote: LessonNote = {
        id: `note-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        lessonId,
        pageId,
        content: content.trim(),
        timestamp: new Date().toISOString(),
        lastModified: new Date().toISOString(),
        tags: tags || [],
        isBookmarked: false,
      };

      setEducationData((currentData) => {
        const updatedNotes = [...currentData.notes, newNote];
        const updatedTags = [
          ...new Set([...currentData.noteTags, ...(tags || [])]),
        ];

        const newData = {
          ...currentData,
          notes: updatedNotes,
          noteTags: updatedTags,
        };

        // Save asynchronously without blocking
        saveData(newData).catch((error) => {
          console.error("Failed to save note:", error);
        });

        return newData;
      });

      return newNote;
    },
    [saveData],
  );

  const updateNote = useCallback(
    async (noteId: string, content: string, tags?: string[]) => {
      setEducationData((currentData) => {
        const noteIndex = currentData.notes.findIndex(
          (note) => note.id === noteId,
        );
        if (noteIndex === -1) return currentData;

        const updatedNotes = [...currentData.notes];
        updatedNotes[noteIndex] = {
          ...updatedNotes[noteIndex],
          content: content.trim(),
          lastModified: new Date().toISOString(),
          tags: tags || updatedNotes[noteIndex].tags,
        };

        const updatedTags = [
          ...new Set([...currentData.noteTags, ...(tags || [])]),
        ];

        const newData = {
          ...currentData,
          notes: updatedNotes,
          noteTags: updatedTags,
        };

        // Save asynchronously without blocking
        saveData(newData).catch((error) => {
          console.error("Failed to update note:", error);
        });

        return newData;
      });
    },
    [saveData],
  );

  const deleteNote = useCallback(
    async (noteId: string) => {
      setEducationData((currentData) => {
        const updatedNotes = currentData.notes.filter(
          (note) => note.id !== noteId,
        );

        // Recalculate tags from remaining notes
        const remainingTags = updatedNotes.flatMap((note) => note.tags || []);
        const updatedTags = [...new Set(remainingTags)];

        const newData = {
          ...currentData,
          notes: updatedNotes,
          noteTags: updatedTags,
        };

        // Save asynchronously without blocking
        saveData(newData).catch((error) => {
          console.error("Failed to delete note:", error);
        });

        return newData;
      });
    },
    [saveData],
  );

  const getNotesForLesson = useCallback(
    (lessonId: string, pageId?: string) => {
      return (
        educationData.notes?.filter(
          (note) =>
            note.lessonId === lessonId && (!pageId || note.pageId === pageId),
        ) || []
      ).sort(
        (a, b) =>
          new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime(),
      );
    },
    [educationData.notes],
  );

  const searchNotes = useCallback(
    (query: string, lessonId?: string) => {
      const lowercaseQuery = query.toLowerCase().trim();
      if (!lowercaseQuery) return [];

      return educationData.notes
        .filter((note) => {
          const matchesLesson = !lessonId || note.lessonId === lessonId;
          const matchesContent = note.content
            .toLowerCase()
            .includes(lowercaseQuery);
          const matchesTags = note.tags?.some((tag) =>
            tag.toLowerCase().includes(lowercaseQuery),
          );

          return matchesLesson && (matchesContent || matchesTags);
        })
        .sort(
          (a, b) =>
            new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime(),
        );
    },
    [educationData.notes],
  );

  const toggleNoteBookmark = useCallback(
    async (noteId: string) => {
      setEducationData((currentData) => {
        const noteIndex = currentData.notes.findIndex(
          (note) => note.id === noteId,
        );
        if (noteIndex === -1) return currentData;

        const updatedNotes = [...currentData.notes];
        updatedNotes[noteIndex] = {
          ...updatedNotes[noteIndex],
          isBookmarked: !updatedNotes[noteIndex].isBookmarked,
        };

        const newData = {
          ...currentData,
          notes: updatedNotes,
        };

        // Save asynchronously without blocking
        saveData(newData).catch((error) => {
          console.error("Failed to toggle note bookmark:", error);
        });

        return newData;
      });
    },
    [saveData],
  );

  const getBookmarkedNotes = useCallback(() => {
    return educationData.notes
      .filter((note) => note.isBookmarked)
      .sort(
        (a, b) =>
          new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime(),
      );
  }, [educationData.notes]);

  // Rating management methods
  const addLessonRating = useCallback(
    async (lessonId: string, rating: number, review?: string) => {
      if (rating < 1 || rating > 5) {
        throw new Error("Rating must be between 1 and 5");
      }

      const newRating: LessonRating = {
        lessonId,
        rating,
        review: review?.trim(),
        timestamp: new Date().toISOString(),
        helpful: false,
      };

      setEducationData((currentData) => {
        // Remove existing rating for this lesson if it exists
        const existingRatings = currentData.ratings.filter(
          (r) => r.lessonId !== lessonId,
        );
        const updatedRatings = [...existingRatings, newRating];

        const newData = {
          ...currentData,
          ratings: updatedRatings,
        };

        // Save asynchronously without blocking
        saveData(newData).catch((error) => {
          console.error("Failed to save rating:", error);
        });

        return newData;
      });

      return newRating;
    },
    [saveData],
  );

  const updateLessonRating = useCallback(
    async (lessonId: string, rating: number, review?: string) => {
      if (rating < 1 || rating > 5) {
        throw new Error("Rating must be between 1 and 5");
      }

      setEducationData((currentData) => {
        const ratingIndex = currentData.ratings.findIndex(
          (r) => r.lessonId === lessonId,
        );
        if (ratingIndex === -1) return currentData;

        const updatedRatings = [...currentData.ratings];
        updatedRatings[ratingIndex] = {
          ...updatedRatings[ratingIndex],
          rating,
          review: review?.trim(),
          timestamp: new Date().toISOString(),
        };

        const newData = {
          ...currentData,
          ratings: updatedRatings,
        };

        // Save asynchronously without blocking
        saveData(newData).catch((error) => {
          console.error("Failed to update rating:", error);
        });

        return newData;
      });
    },
    [saveData],
  );

  const getLessonRating = useCallback(
    (lessonId: string) => {
      return (
        educationData.ratings?.find((r) => r.lessonId === lessonId) || null
      );
    },
    [educationData.ratings],
  );

  const deleteLessonRating = useCallback(
    async (lessonId: string) => {
      setEducationData((currentData) => {
        const updatedRatings = currentData.ratings.filter(
          (r) => r.lessonId !== lessonId,
        );

        const newData = {
          ...currentData,
          ratings: updatedRatings,
        };

        // Save asynchronously without blocking
        saveData(newData).catch((error) => {
          console.error("Failed to delete rating:", error);
        });

        return newData;
      });
    },
    [saveData],
  );

  const toggleRatingHelpful = useCallback(
    async (lessonId: string) => {
      setEducationData((currentData) => {
        const ratingIndex = currentData.ratings.findIndex(
          (r) => r.lessonId === lessonId,
        );
        if (ratingIndex === -1) return currentData;

        const updatedRatings = [...currentData.ratings];
        updatedRatings[ratingIndex] = {
          ...updatedRatings[ratingIndex],
          helpful: !updatedRatings[ratingIndex].helpful,
        };

        const newData = {
          ...currentData,
          ratings: updatedRatings,
        };

        // Save asynchronously without blocking
        saveData(newData).catch((error) => {
          console.error("Failed to toggle rating helpful:", error);
        });

        return newData;
      });
    },
    [saveData],
  );

  const getAllRatings = useCallback(() => {
    return educationData.ratings.sort(
      (a, b) =>
        new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime(),
    );
  }, [educationData.ratings]);

  // Survey management methods
  const addLessonSurvey = useCallback(
    async (
      lessonId: string,
      surveyId: string,
      responses: LessonSurvey["responses"],
      timeSpent: number,
    ) => {
      const newSurvey: LessonSurvey = {
        lessonId,
        surveyId,
        responses,
        completedAt: new Date().toISOString(),
        timeSpent,
      };

      setEducationData((currentData) => {
        // Remove existing survey for this lesson if it exists
        const existingSurveys = currentData.surveys.filter(
          (s) => s.lessonId !== lessonId,
        );
        const updatedSurveys = [...existingSurveys, newSurvey];

        const newData = {
          ...currentData,
          surveys: updatedSurveys,
        };

        // Save asynchronously without blocking
        saveData(newData).catch((error) => {
          console.error("Failed to save survey:", error);
        });

        return newData;
      });

      return newSurvey;
    },
    [saveData],
  );

  const getLessonSurvey = useCallback(
    (lessonId: string) => {
      return educationData.surveys.find((s) => s.lessonId === lessonId) || null;
    },
    [educationData.surveys],
  );

  const deleteLessonSurvey = useCallback(
    async (lessonId: string) => {
      setEducationData((currentData) => {
        const updatedSurveys = currentData.surveys.filter(
          (s) => s.lessonId !== lessonId,
        );

        const newData = {
          ...currentData,
          surveys: updatedSurveys,
        };

        // Save asynchronously without blocking
        saveData(newData).catch((error) => {
          console.error("Failed to delete survey:", error);
        });

        return newData;
      });
    },
    [saveData],
  );

  const getAllSurveys = useCallback(() => {
    return educationData.surveys.sort(
      (a, b) =>
        new Date(b.completedAt).getTime() - new Date(a.completedAt).getTime(),
    );
  }, [educationData.surveys]);

  // Comment management methods
  const addLessonComment = useCallback(
    async (lessonId: string, content: string) => {
      const newComment: LessonComment = {
        id: `comment-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        lessonId,
        content: content.trim(),
        timestamp: new Date().toISOString(),
        lastModified: new Date().toISOString(),
        isPinned: false,
        replies: [],
      };

      setEducationData((currentData) => {
        const updatedComments = [...currentData.comments, newComment];

        const newData = {
          ...currentData,
          comments: updatedComments,
        };

        // Save asynchronously without blocking
        saveData(newData).catch((error) => {
          console.error("Failed to save comment:", error);
        });

        return newData;
      });

      return newComment;
    },
    [saveData],
  );

  const updateLessonComment = useCallback(
    async (commentId: string, content: string) => {
      setEducationData((currentData) => {
        const commentIndex = currentData.comments.findIndex(
          (comment) => comment.id === commentId,
        );
        if (commentIndex === -1) return currentData;

        const updatedComments = [...currentData.comments];
        updatedComments[commentIndex] = {
          ...updatedComments[commentIndex],
          content: content.trim(),
          lastModified: new Date().toISOString(),
        };

        const newData = {
          ...currentData,
          comments: updatedComments,
        };

        // Save asynchronously without blocking
        saveData(newData).catch((error) => {
          console.error("Failed to update comment:", error);
        });

        return newData;
      });
    },
    [saveData],
  );

  const deleteLessonComment = useCallback(
    async (commentId: string) => {
      setEducationData((currentData) => {
        const updatedComments = currentData.comments.filter(
          (comment) => comment.id !== commentId,
        );

        const newData = {
          ...currentData,
          comments: updatedComments,
        };

        // Save asynchronously without blocking
        saveData(newData).catch((error) => {
          console.error("Failed to delete comment:", error);
        });

        return newData;
      });
    },
    [saveData],
  );

  const getLessonComments = useCallback(
    (lessonId: string) => {
      const comments = educationData.comments || [];
      return comments
        .filter((comment) => comment.lessonId === lessonId)
        .sort((a, b) => {
          // Pinned comments first, then by timestamp
          if (a.isPinned && !b.isPinned) return -1;
          if (!a.isPinned && b.isPinned) return 1;
          return (
            new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
          );
        });
    },
    [educationData.comments],
  );

  const toggleCommentPin = useCallback(
    async (commentId: string) => {
      setEducationData((currentData) => {
        const commentIndex = currentData.comments.findIndex(
          (comment) => comment.id === commentId,
        );
        if (commentIndex === -1) return currentData;

        const updatedComments = [...currentData.comments];
        updatedComments[commentIndex] = {
          ...updatedComments[commentIndex],
          isPinned: !updatedComments[commentIndex].isPinned,
        };

        const newData = {
          ...currentData,
          comments: updatedComments,
        };

        // Save asynchronously without blocking
        saveData(newData).catch((error) => {
          console.error("Failed to toggle comment pin:", error);
        });

        return newData;
      });
    },
    [saveData],
  );

  // Get lesson progress
  const getLessonProgress = useCallback(
    (lessonId: string) => {
      return educationData.progress[lessonId] || undefined;
    },
    [educationData.progress],
  );

  return {
    // Core data
    educationData,
    isLoading,

    // Analytics (if enabled)
    analytics,

    // Sync status
    isSyncing,
    syncError,

    // Core methods (backward compatible)
    updateLessonProgress,
    markLessonCompleted,
    addQuizScore,
    addBookmark,
    removeBookmark,
    isBookmarked,

    // Enhanced methods
    syncWithCloud,
    createBackup,
    restoreBackup,
    resetProgress,
    getLessonProgress,

    // Flow control methods
    checkPrerequisites,
    getPrerequisiteStatus,
    canAccessLesson,
    validateLessonCompletion,
    getLessonFlowStatus,
    updatePageCompletion,
    canNavigateToPage,

    // Note management methods
    addNote,
    updateNote,
    deleteNote,
    getNotesForLesson,
    searchNotes,
    toggleNoteBookmark,
    getBookmarkedNotes,

    // Rating management methods
    addLessonRating,
    updateLessonRating,
    getLessonRating,
    deleteLessonRating,
    toggleRatingHelpful,
    getAllRatings,

    // Survey management methods
    addLessonSurvey,
    getLessonSurvey,
    deleteLessonSurvey,
    getAllSurveys,

    // Comment management methods
    addLessonComment,
    updateLessonComment,
    deleteLessonComment,
    getLessonComments,
    toggleCommentPin,

    // Skill assessment (if enabled)
    getSkillMetrics,
    getMasteryScore,
    getLearningRecommendations,

    // Export/Import
    exportProgress,
    importProgress,
    exportProgressData,
    importProgressData,
  };
};
